import { BaseAdapter } from "./base-adapter";
import type { TEvent } from "../types";
import { EventType } from "../types";

/**
 * Configuration interface for Kwik Checkout Adapter
 */
export interface KwikCheckoutConfig {
  mid?: string;
  environment?: string;
  storeId?: string;
  enableDebugLogs?: boolean;
}

/**
 * Kwik Checkout Analytics Adapter for tracking checkout events to GoKwik platform
 */
export class KwikCheckoutAdapter extends BaseAdapter {
  public readonly name = "KwikCheckout";
  private mid: string;
  private environment: string;
  private storeId: string;

  constructor(config: KwikCheckoutConfig) {
    super(config);
    this.mid = config.mid || "";
    this.environment = config.environment || "production";
    this.storeId = config.storeId || "";
    // Initialize logger with proper adapter name
    this.initializeLogger();
  }

  /**
   * Initialize the GoKwik adapter
   */
  public async initialize(): Promise<void> {
    try {
      if (typeof window !== "undefined") {
        // Initialize GoKwik SDK if available
        this.logger.info("GoKwik adapter initialized successfully", {
          mid: this.mid,
          environment: this.environment,
          storeId: this.storeId,
        });
      }
      this.initialized = true;
    } catch (error) {
      this.logger.error("Failed to initialize GoKwik adapter", error as Error);
      throw error;
    }
  }

  /**
   * Track an event using GoKwik
   */
  public async trackEvent(event: TEvent): Promise<void> {
    if (!this.initialized || typeof window === "undefined") {
      this.logger.warn(
        "GoKwik adapter not initialized or not in browser environment"
      );
      return;
    }

    try {
      this.logger.debug("Processing event", { eventType: event.type });

      // Enhanced event data with affiliate parameters
      const enhancedEvent = this.enhanceWithAffiliateParams(event);

      switch (event.type) {
        case EventType.STARTED_CHECKOUT_GK:
          this.trackStartedCheckoutGK(enhancedEvent);
          break;

        case EventType.MOBILE_ADDED_GK:
          this.trackMobileAddedGK(enhancedEvent);
          break;

        case EventType.ADDRESS_SELECTED_GK:
          this.trackAddressSelectedGK(enhancedEvent);
          break;

        case EventType.ADDRESS_COMPLETED_GK:
          this.trackAddressCompletedGK(enhancedEvent);
          break;

        case EventType.ADDRESS_ADDED_GK:
          this.trackAddressAddedGK(enhancedEvent);
          break;

        case EventType.PIN_CODE_ADDED_GK:
          this.trackPinCodeAddedGK(enhancedEvent);
          break;

        case EventType.PAYMENT_METHOD_SELECTED_GK:
          this.trackPaymentMethodSelectedGK(enhancedEvent);
          break;

        case EventType.PAYMENT_COMPLETED_GK:
          this.trackPaymentCompletedGK(enhancedEvent);
          break;

        case EventType.ORDER_SUCCESS:
          this.trackOrderSuccess(enhancedEvent);
          break;

        case EventType.ORDER_COMPLETED:
          this.trackOrderCompleted(enhancedEvent);
          break;

        case EventType.CART_VIEWED:
          this.trackCartViewed(enhancedEvent);
          break;

        case EventType.CHECKOUT_STARTED:
          this.trackCheckoutStarted(enhancedEvent);
          break;

        case EventType.CHECKOUT_COMPLETED:
          this.trackCheckoutCompleted(enhancedEvent);
          break;

        case EventType.ADD_PAYMENT_INFO:
          this.trackAddPaymentInfo(enhancedEvent);
          break;

        default:
          this.logger.debug("Event not supported", { eventType: event.type });
          break;
      }
    } catch (error) {
      this.logger.error("Error tracking GoKwik event", error as Error, {
        eventType: event.type,
      });
    }
  }

  /**
   * Send event to GoKwik platform
   */
  private sendGoKwikEvent(eventName: string, eventData: any): void {
    this.logger.debug(`Sending ${eventName}`, { eventData });

    // Check if GoKwik SDK is available
    if (typeof window !== "undefined" && (window as any).gokwikSdk) {
      try {
        // Send event through GoKwik SDK
        (window as any).gokwikSdk.track?.(eventName, eventData);
        this.logger.debug(`${eventName} sent via SDK successfully`);
      } catch (error) {
        this.logger.error(
          `Error sending GoKwik event ${eventName} via SDK`,
          error as Error
        );
      }
    } else {
      // Fallback: dispatch custom event for GoKwik to listen
      if (typeof window !== "undefined") {
        const customEvent = new CustomEvent(`gokwik_${eventName}`, {
          detail: eventData,
        });
        window.dispatchEvent(customEvent);
        this.logger.debug(`${eventName} sent via custom event successfully`);
      }
    }
  }

  /**
   * Track started checkout GK event
   */
  private trackStartedCheckoutGK(event: any): void {
    this.sendGoKwikEvent("started_checkout", {
      cart_value: event.cartValue,
      currency: event.currency,
      timestamp: event.timestamp || Date.now(),
    });
  }

  /**
   * Track mobile added GK event
   */
  private trackMobileAddedGK(event: any): void {
    this.sendGoKwikEvent("mobile_added", {
      mobile: event.mobile,
      timestamp: event.timestamp || Date.now(),
    });
  }

  /**
   * Track address selected GK event
   */
  private trackAddressSelectedGK(event: any): void {
    this.sendGoKwikEvent("address_selected", {
      address_id: event.addressId,
      timestamp: event.timestamp || Date.now(),
    });
  }

  /**
   * Track address completed GK event
   */
  private trackAddressCompletedGK(event: any): void {
    this.sendGoKwikEvent("address_completed", {
      address_id: event.addressId,
      timestamp: event.timestamp || Date.now(),
    });
  }

  /**
   * Track address added GK event
   */
  private trackAddressAddedGK(event: any): void {
    this.sendGoKwikEvent("address_added", {
      address_id: event.addressId,
      timestamp: event.timestamp || Date.now(),
    });
  }

  /**
   * Track PIN code added GK event
   */
  private trackPinCodeAddedGK(event: any): void {
    this.sendGoKwikEvent("pin_code_added", {
      pin_code: event.pinCode,
      timestamp: event.timestamp || Date.now(),
    });
  }

  /**
   * Track payment method selected GK event
   */
  private trackPaymentMethodSelectedGK(event: any): void {
    this.sendGoKwikEvent("payment_method_selected", {
      payment_method: event.paymentMethod,
      timestamp: event.timestamp || Date.now(),
    });
  }

  /**
   * Track payment completed GK event
   */
  private trackPaymentCompletedGK(event: any): void {
    this.sendGoKwikEvent("payment_completed", {
      amount: event.amount,
      currency: event.currency,
      timestamp: event.timestamp || Date.now(),
    });
  }

  /**
   * Track order success event
   */
  private trackOrderSuccess(event: any): void {
    this.sendGoKwikEvent("order_success", {
      order_id: event.orderId,
      amount: event.amount,
      currency: event.currency,
      timestamp: event.timestamp || Date.now(),
    });
  }

  /**
   * Track order completed event
   */
  private trackOrderCompleted(event: any): void {
    this.sendGoKwikEvent("order_completed", {
      order_id: event.orderId,
      amount: event.amount,
      currency: event.currency,
      timestamp: event.timestamp || Date.now(),
    });
  }

  /**
   * Track cart viewed event
   */
  private trackCartViewed(event: any): void {
    this.sendGoKwikEvent("cart_viewed", {
      cart_id: event.cartId,
      products: event.products,
      timestamp: event.timestamp || Date.now(),
    });
  }

  /**
   * Track checkout started event
   */
  private trackCheckoutStarted(event: any): void {
    this.sendGoKwikEvent("checkout_started", {
      cart_value: event.cartValue,
      currency: event.currency,
      item_count: event.itemCount,
      items: event.items,
      timestamp: event.timestamp || Date.now(),
    });
  }

  /**
   * Track checkout completed event
   */
  private trackCheckoutCompleted(event: any): void {
    this.sendGoKwikEvent("checkout_completed", {
      order_id: event.orderId,
      cart_value: event.cartValue,
      currency: event.currency,
      item_count: event.itemCount,
      items: event.items,
      timestamp: event.timestamp || Date.now(),
    });
  }

  /**
   * Track add payment info event
   */
  private trackAddPaymentInfo(event: any): void {
    this.sendGoKwikEvent("add_payment_info", {
      cart_value: event.cartValue,
      currency: event.currency,
      item_count: event.itemCount,
      payment_type: event.paymentType,
      items: event.items,
      timestamp: event.timestamp || Date.now(),
    });
  }
}
